import { useState } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { reportsMonthlyShowPath } from '@/shared/lib/routes'
import { Button } from '@/shared/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert'
import ReportStatusBadge from '@/pages/Reports/components/ReportStatusBadge'
import { ReportEmptyState } from '@/pages/Reports/components/ReportEmptyState'
import DashboardTabs from '@/pages/Compliance/DashboardTabs'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

interface SignerStatus {
  userId: number
  userName: string
  sortOrder: number
  signed: boolean
}

interface RejectionAlert {
  rejectedBy: string | null
  rejectedAt: string
  comments: string | null
}

interface PendingReport {
  id: number
  departmentId: number | null
  departmentName: string | null
  siteName: string | null
  reportStartDateFormatted: string
  compliancePercent: number | null
  statusCode: string | null
  signedAt: string | null
  chainSigners: SignerStatus[]
  rejectionAlert: RejectionAlert | null
}

interface Props {
  pendingReports: PendingReport[]
  error?: string
  [key: string]: unknown
}

const SignersChain = ({ signers }: { signers: SignerStatus[] }) => (
  <div className="mt-2 space-y-1">
    <p className="text-xs font-medium text-muted-foreground">{t('approvals.chainStatus')}</p>
    <ol className="space-y-0.5">
      {signers.map((signer) => (
        <li key={signer.userId} className="flex items-center gap-2 text-sm">
          <span className={signer.signed ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
            {signer.sortOrder}. {signer.userName}
          </span>
          {signer.signed && <span className="text-xs text-green-600">✓</span>}
        </li>
      ))}
    </ol>
  </div>
)

const PendingApprovalCard = ({ report }: { report: PendingReport }) => {
  const { formatDateTime } = useSiteDateTime()
  const [rejectionAlertDismissed, setRejectionAlertDismissed] = useState(false)

  const complianceLabel = report.compliancePercent != null ? `${report.compliancePercent}%` : '—'

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-5 space-y-3">
      {/* Rejection-alert banner */}
      {report.rejectionAlert && !rejectionAlertDismissed && (
        <Alert variant="destructive">
          <AlertTitle className="flex items-center justify-between">
            <span>{t('approvals.rejectionAlertTitle')}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-destructive hover:text-destructive"
              onClick={() => setRejectionAlertDismissed(true)}
            >
              {t('approvals.rejectionAlertDismiss')}
            </Button>
          </AlertTitle>
          <AlertDescription className="space-y-1 mt-1">
            {report.rejectionAlert.rejectedBy && (
              <p className="text-sm font-medium">{report.rejectionAlert.rejectedBy}</p>
            )}
            {report.rejectionAlert.comments && (
              <p className="text-sm">{report.rejectionAlert.comments}</p>
            )}
            <p className="text-xs opacity-75">{formatDateTime(report.rejectionAlert.rejectedAt)}</p>
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold">{report.departmentName ?? '—'}</h3>
          <ReportStatusBadge
            record={{
              statusCode: report.statusCode,
              statusLabel: null,
              isSigned: report.signedAt != null,
            }}
          />
        </div>
        {report.siteName && <p className="text-sm text-muted-foreground">{report.siteName}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span>{report.reportStartDateFormatted}</span>
        <span>
          <span className="font-medium">{t('reports.monthly.compliancePercent')}: </span>
          {complianceLabel}
        </span>
      </div>

      {report.chainSigners.length > 0 && <SignersChain signers={report.chainSigners} />}

      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" asChild>
          <Link href={reportsMonthlyShowPath({ id: report.id })}>{t('approvals.viewNow')}</Link>
        </Button>
      </div>
    </div>
  )
}

const PendingApprovalsPage = () => {
  const { pendingReports, error } = usePage<Props>().props

  useBreadcrumbs([{ label: t('nav.dashboard') }, { label: t('nav.pendingApprovals') }])

  return (
    <div className="space-y-4 sm:space-y-6">
      <DashboardTabs />
      <h1 className="text-xl font-bold sm:text-2xl">{t('approvals.title')}</h1>

      {error && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {pendingReports.length === 0 ? (
        <ReportEmptyState message={t('approvals.noPending')} />
      ) : (
        <div className="space-y-4">
          {pendingReports.map((report) => (
            <PendingApprovalCard key={report.id} report={report} />
          ))}
        </div>
      )}
    </div>
  )
}

export default PendingApprovalsPage
